import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { presseData } from '../data/newModules';
import AnimatedSection from '../components/common/AnimatedSection';
import SocialShare from '../components/common/SocialShare';
import { 
  Newspaper, Download, Mail, Phone, MessageCircle, Image,
  FileText, Copy, Check, ArrowRight, ExternalLink, Share2
} from 'lucide-react';
import { useState } from 'react';

const PressePage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const content = presseData[language] || presseData.fr;
  const [copiedMessage, setCopiedMessage] = useState(null);

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedMessage(index);
    setTimeout(() => setCopiedMessage(null), 2000);
  };

  return (
    <div className="pt-20">
      {/* Hero */}
      <section className={`py-16 ${isDark ? 'bg-[#1C1917]' : 'bg-gradient-to-br from-[#F5F0E8] to-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <Newspaper className={`w-16 h-16 mx-auto mb-6 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`} />
              <h1 className="heading-1 mb-4">{content.title}</h1>
              <div className="divider mx-auto mb-6" />
              <p className="body-large">{content.subtitle}</p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Bio Section */}
      <section className={`section ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <h2 className="heading-2 mb-8">Biographies</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Short Bio */}
                <div className={`rounded-xl p-6 ${isDark ? 'bg-[#1C1917]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`font-semibold ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`}>Version courte (100 mots)</h3>
                    <button 
                      onClick={() => copyToClipboard(content.bio.short, 'short')}
                      className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-stone-100'}`}
                    >
                      {copiedMessage === 'short' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-theme-muted" />}
                    </button>
                  </div>
                  <p className="text-sm text-theme-secondary leading-relaxed">{content.bio.short}</p>
                </div>

                {/* Long Bio */}
                <div className={`rounded-xl p-6 ${isDark ? 'bg-[#1C1917]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className={`font-semibold ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`}>Version longue (300 mots)</h3>
                    <button 
                      onClick={() => copyToClipboard(content.bio.long, 'long')}
                      className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-stone-100'}`}
                    >
                      {copiedMessage === 'long' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-theme-muted" />}
                    </button>
                  </div>
                  <p className="text-sm text-theme-secondary leading-relaxed line-clamp-6">{content.bio.long}</p>
                  <button className={`mt-2 text-sm font-medium ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`}>
                    Voir tout...
                  </button>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Key Messages */}
      <section className={`section ${isDark ? 'bg-[#1C1917]' : 'bg-[#F5F0E8]'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <h2 className="heading-2 mb-8">Messages clés</h2>
              
              <div className="space-y-4">
                {content.keyMessages.map((message, index) => (
                  <div 
                    key={index}
                    className={`rounded-xl p-5 flex items-start justify-between gap-4 ${isDark ? 'bg-[#0C0A09]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}
                  >
                    <div className="flex items-start gap-4 flex-1">
                      <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isDark ? 'bg-[#5B2D8E]/20 text-[#9B7FCE]' : 'bg-[#5B2D8E]/10 text-[#5B2D8E]'}`}>
                        {index + 1}
                      </span>
                      <p className="text-theme-primary font-medium" style={{ fontFamily: 'var(--font-serif)' }}>
                        {message}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <SocialShare
                        title={`Message clé ADA #${index + 1}`}
                        text={message}
                        url={`${window.location.origin}/presse`}
                        hashtags={['ADACI', 'CotedIvoire']}
                        compact={true}
                      />
                      <button 
                        onClick={() => copyToClipboard(message, `msg-${index}`)}
                        className={`p-2 rounded-lg ${isDark ? 'hover:bg-white/10' : 'hover:bg-stone-100'}`}
                        title="Copier le texte"
                      >
                        {copiedMessage === `msg-${index}` ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-theme-muted" />}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Assets */}
      <section className={`section ${isDark ? 'bg-[#0C0A09]' : 'bg-[#FAFAF9]'}`}>
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <AnimatedSection>
              <h2 className="heading-2 mb-8">Ressources visuelles</h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                {/* Logos */}
                <div>
                  <h3 className={`font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`}>
                    <FileText className="w-5 h-5" /> Logos
                  </h3>
                  <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-[#1C1917]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                    {content.assets.logos.map((logo, index) => (
                      <div 
                        key={index}
                        className={`p-4 flex items-center justify-between ${index < content.assets.logos.length - 1 ? `border-b ${isDark ? 'border-white/10' : 'border-stone-100'}` : ''}`}
                      >
                        <div>
                          <span className="font-medium text-theme-primary">{logo.name}</span>
                          <span className={`block text-xs ${isDark ? 'text-stone-500' : 'text-stone-400'}`}>{logo.format} • {logo.size}</span>
                        </div>
                        <button 
                          className={`px-3 py-1.5 rounded-lg text-sm ${isDark ? 'bg-white/10 text-stone-300' : 'bg-stone-100 text-stone-600'}`}
                          disabled
                        >
                          Bientôt
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Photos */}
                <div>
                  <h3 className={`font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-[#FFC107]' : 'text-[#5B2D8E]'}`}>
                    <Image className="w-5 h-5" /> Photos officielles
                  </h3>
                  <div className={`rounded-xl overflow-hidden ${isDark ? 'bg-[#1C1917]' : 'bg-white'} border ${isDark ? 'border-white/10' : 'border-stone-200'}`}>
                    {content.assets.photos.map((photo, index) => (
                      <div 
                        key={index}
                        className={`p-4 flex items-center justify-between ${index < content.assets.photos.length - 1 ? `border-b ${isDark ? 'border-white/10' : 'border-stone-100'}` : ''}`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-lg ${isDark ? 'bg-white/10' : 'bg-stone-100'} flex items-center justify-center`}>
                            <Image className="w-6 h-6 text-theme-muted" />
                          </div>
                          <div>
                            <span className="font-medium text-theme-primary">{photo.name}</span>
                            <span className={`block text-xs ${isDark ? 'text-stone-500' : 'text-stone-400'}`}>{photo.description}</span>
                          </div>
                        </div>
                        <button 
                          className={`px-3 py-1.5 rounded-lg text-sm ${isDark ? 'bg-white/10 text-stone-300' : 'bg-stone-100 text-stone-600'}`}
                          disabled
                        >
                          Bientôt
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Usage Guidelines */}
              <div className={`mt-8 p-5 rounded-xl ${isDark ? 'bg-[#FFC107]/10 border-[#FFC107]/30' : 'bg-[#FFC107]/5 border-[#FFC107]/20'} border`}>
                <h4 className={`font-semibold mb-2 ${isDark ? 'text-[#FFC107]' : 'text-[#B8860B]'}`}>Règles d'utilisation</h4>
                <p className="text-sm text-theme-secondary">{content.assets.guidelines}</p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Contact Press */}
      <section className="py-16 bg-gradient-to-br from-[#5B2D8E] to-[#7C4DAF]">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <AnimatedSection>
              <h2 className="heading-2 text-white mb-8">Contact Presse</h2>
              
              <div className="flex flex-wrap justify-center gap-4 mb-8">
                <a 
                  href={`mailto:${content.contact.email}`}
                  className="flex items-center gap-2 px-5 py-3 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors"
                >
                  <Mail className="w-5 h-5" />
                  {content.contact.email}
                </a>
                {content.contact.phone && (
                  <a 
                    href={`tel:${content.contact.phone}`}
                    className="flex items-center gap-2 px-5 py-3 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    {content.contact.phone}
                  </a>
                )}
                {content.contact.whatsapp && (
                  <a 
                    href={`https://wa.me/${content.contact.whatsapp.replace(/\s/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-5 py-3 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors"
                  >
                    <MessageCircle className="w-5 h-5" />
                    WhatsApp
                  </a>
                )}
              </div>

              <button className="btn-secondary" disabled>
                <Download className="w-5 h-5" />
                {content.cta.downloadKit}
                <span className="text-xs opacity-60">(Bientôt)</span>
              </button>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PressePage;
